import { useEffect, useRef, useState } from "react";

import { obterImu, obterSerie, obterStatus } from "../api/client";
import type { ImuPoint, Series, StatusPoint } from "../types";

/** O que se sabe de uma boia no intervalo escolhido. */
export interface Historico {
  temperatura: Series | null;
  sonar: Series | null;
  status: StatusPoint[];
  imu: ImuPoint[];
  /** Alguma das séries lentas bateu no teto de páginas. */
  truncado: boolean;
}

export interface Historicos {
  /** Uma entrada por boia pedida, na mesma ordem. */
  por: Record<string, Historico>;
  erro: string | null;
  aRecarregar: boolean;
}

const VAZIO: Historicos = { por: {}, erro: null, aRecarregar: false };

/**
 * Histórico das boias selecionadas, para o intervalo escolhido.
 *
 * **A primeira boia leva as quatro grandezas; as restantes só temperatura e
 * sonar.** Comparar ondulação e tensão entre boias não diz nada de útil — são
 * grandezas operacionais de cada nó — e trazê-las multiplicava os pedidos por
 * quatro. As duas que ficam são as que a comparação existe para ver: os
 * gradientes entre nós.
 *
 * Tudo em paralelo: são pedidos independentes e em série somavam-se.
 *
 * Nada de "skeleton": em recarga guarda-se o desenho anterior e baixa-se a
 * opacidade. Trocar os gráficos por caixas cinzentas a cada mudança de
 * intervalo faz a página saltar e lê-se pior.
 */
export function useHistory(codes: string[], de: Date, ate: Date): Historicos {
  const [dados, setDados] = useState<Historicos>(VAZIO);
  const abortar = useRef<AbortController | null>(null);

  const deMs = de.getTime();
  const ateMs = ate.getTime();
  // Chave estável: sem isto, um array novo a cada render voltava a disparar o
  // efeito de cada vez que o pai se redesenhasse.
  const chave = codes.join(",");

  useEffect(() => {
    const lista = chave === "" ? [] : chave.split(",");
    if (lista.length === 0) {
      setDados(VAZIO);
      return;
    }

    abortar.current?.abort();
    const controlo = new AbortController();
    abortar.current = controlo;
    const inicio = new Date(deMs);
    const fim = new Date(ateMs);

    setDados((a) => ({ ...a, aRecarregar: true }));

    void (async () => {
      try {
        const resultados = await Promise.all(
          lista.map(async (code, posicao): Promise<[string, Historico]> => {
            const completo = posicao === 0;
            const [temperatura, sonar, status, imu] = await Promise.all([
              obterSerie(code, "temperature", inicio, fim, controlo.signal),
              obterSerie(code, "sonar", inicio, fim, controlo.signal),
              completo
                ? obterStatus(code, inicio, fim, controlo.signal)
                : Promise.resolve({ itens: [], truncado: false }),
              completo
                ? obterImu(code, inicio, fim, controlo.signal)
                : Promise.resolve({ itens: [], truncado: false }),
            ]);
            return [
              code,
              {
                temperatura,
                sonar,
                status: status.itens,
                imu: imu.itens,
                truncado: status.truncado || imu.truncado,
              },
            ];
          }),
        );

        setDados({ por: Object.fromEntries(resultados), erro: null, aRecarregar: false });
      } catch (erro) {
        if (controlo.signal.aborted) return;
        setDados((a) => ({
          ...a,
          erro: erro instanceof Error ? erro.message : "Falha a ler o histórico",
          aRecarregar: false,
        }));
      }
    })();

    return () => controlo.abort();
  }, [chave, deMs, ateMs]);

  return dados;
}
